import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/blocks.action'
import { validateEvaluationConfig } from 'helpers/validators.helper'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import { EditEvaluation } from 'models/evaluation.model'
import { QuestionRating } from 'models/question.model'
import { EvaluationSteps } from '../../../constants'

interface IUseStep {
  data: EditEvaluation
  editedQuestions: QuestionRating[]
  onClose: () => void
  save: () => void
}

export const useStep = ({ data, editedQuestions, onClose, save }: IUseStep) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [step, setStep] = useState<EvaluationSteps>(EvaluationSteps.DETAILS)
  const lastStep = useRef(step)

  const withChanges = useRef<Partial<Record<EvaluationSteps, boolean>>>({})
  const { list } = useSelector(({ questions }: IMainState) => questions)
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)

  useEffect(() => {
    lastStep.current = step
  }, [step])

  const resetChanges = useCallback((key: EvaluationSteps) => {
    withChanges.current[key] = false
  }, [])

  const setChanges = useCallback((key: EvaluationSteps) => {
    withChanges.current[key] = true
  }, [])

  const canShowOptions = useCallback(() => {
    const currentStep = lastStep.current
    // Details controls its own modal where it warns of changes
    if (currentStep === EvaluationSteps.DETAILS) return false
    return !!withChanges.current[currentStep]
  }, [])

  const hasChanges = useCallback(
    (opts?: { validAll?: boolean }) => {
      if (opts?.validAll) {
        const values = Object.values(withChanges.current)
        return values.some(Boolean)
      }
      return canShowOptions()
    },
    [canShowOptions, withChanges]
  )

  const onSaveAndChangeStep = useCallback(
    (newStep: number) => {
      const { id, lessonId } = edit
      switch (step) {
        case 0:
          dispatch({
            type: fromActions.EDIT_EVALUATION_BLOCK,
            payload: { ...data, id, lessonId },
          })
          setStep(newStep)
          break
        case 1: {
          setStep(newStep)
          break
        }
        case 2: {
          const canContinue = validateEvaluationConfig(
            data,
            editedQuestions,
            list
          )
          if (!canContinue) {
            ErrorToast(
              t(
                'administration.blocks.type.evaluation.toasts.error.pointsToPass'
              )
            )
            return
          }
          save()
          setStep(newStep)
          break
        }
        default:
          onClose()
          break
      }
    },
    [data, dispatch, edit, editedQuestions, list, onClose, save, step, t]
  )

  return {
    step,
    onSaveAndChangeStep,
    setChanges,
    setStep,
    withChanges,
    resetChanges,
    canShowOptions,
    hasChanges,
  }
}
